import React from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router';
import Cookies from 'universal-cookie';

import { ThemeOptions, useCompanyQuery, useRestaurantQuery } from '@local/api';
import { isMarketplacePlatform, isYelpPlatform } from '@local/utils';

import { getUseHighContrast } from 'helpers/customer';

import { Theme, ThemeContext, THEMES } from './ThemeContext';

/**
 * Syncs the active theme to the matched route's `restaurantId` param and the
 * restaurant/company theme data
 * Rendered as a pathless layout route so it wraps every page with `useParams()` reactivity.
 */
export const ThemeRouteSync: React.FunctionComponent = () => {
  const { setTheme } = React.useContext(ThemeContext);

  const { hqId, restaurantId, orderId } = useParams();
  const [searchParams] = useSearchParams();
  const companyIdFromQuery = searchParams.get('company');

  const { data: restaurant } = useRestaurantQuery(restaurantId || '', !orderId);
  const { data: company } = useCompanyQuery(
    hqId || companyIdFromQuery || '',
    null,
    null,
    !orderId && !restaurantId
  );

  React.useLayoutEffect(() => {
    // don't set the theme when we're on the order confirmation route this is an odd case and is handled in the confirmation component for now
    if (!orderId) {
      const restaurantTheme = restaurant?.ordering_theme;
      const companyTheme = company?.ordering_theme;
      const activeTheme = restaurantId ? restaurantTheme : companyTheme;
      setTheme(getThemeName(activeTheme));
    }
  }, [orderId, restaurant?.ordering_theme, company?.ordering_theme, setTheme]);

  return <Outlet />;
};

export const getThemeName = (themeOptions: ThemeOptions | undefined): Theme => {
  if (isMarketplacePlatform()) {
    return 'marketplace';
  }

  if (isYelpPlatform()) {
    return 'yelp';
  }

  if (getUseHighContrast()) {
    return 'vanilla';
  }

  const cookies = new Cookies();

  return (
    parseTheme(cookies.get('cn_theme') as string) ||
    parseTheme(themeOptions?.light.surface_color.label.toLowerCase()) ||
    'vanilla'
  );
};

const parseTheme = (possibleTheme: string | undefined): Theme | undefined => {
  return THEMES.find((validTheme) => validTheme === possibleTheme);
};
